// ============================================================================
// Time & Labor — the In/Out Activity report, as rules (item 885, 2026-10-16).
//
// Antonela's spec of 2026-10-13: payroll gets a per-punch report laid out
// like Buddy Punch's In/Out Activity export, beside the Payroll Summary, so
// nobody relearns anything when Buddy Punch goes. TCS App punches only.
//
// ⛔ PURE ON PURPOSE. No value imports: the page and `smoke:inout-activity`
// both run THIS file (Node strips the types), so the numbers the smoke proves
// are the numbers the page shows. The page hands in the formatters from
// siteTime.ts and time-vocabulary.ts; the smoke imports the same two files.
//
// The rules, each one a place the spec would have come out wrong if built
// literally (prompt §2 "Build rules"):
//   - a void shift is left out by the SHIFT's status, never a punch's reason
//     (William Rodriguez's 10/1 0-minute pair carries no reason on either punch)
//   - a shift belongs to its own shift_date (the day it was clocked in)
//   - Reg = paid minutes ÷ 60 on the Out row; the day's Total on the day's last
//     Out row; the Total row from the MINUTES, so it equals the Payroll Summary
//   - "auto-closed" comes from the app's own record of it (the out punch's
//     capture mode, or the daily-ceiling exception) — never from a 12:00 AM
//     guess, which would flag Jose Armando Barahona's real 10/2 12:00:08 AM out
//   - "no building" is the shift having no facility
// ============================================================================

/** One shift, as v_time_segments_detail hands it over. */
export interface InOutSegment {
  segment_id: string;
  worker_id: string;
  facility_id: string | null;
  place_display: string | null;
  shift_date: string;
  started_at: string | null;
  ended_at: string | null;
  paid_minutes: number | null;
  segment_status: string;
  in_event_id: string | null;
  out_event_id: string | null;
  site_tz: string | null;
}

/** One punch, from punch_events. */
export interface InOutPunch {
  punch_event_id: string;
  event_type: string;
  event_at: string;
  capture_mode: string | null;
  ip_address: string | null;
  photo_url: string | null;
  supersedes_event_id: string | null;
  void_reason: string | null;
}

/** One person, from workers + worker_role_codes. */
export interface InOutWorker {
  worker_id: string;
  employee_id: string | null;
  first_name: string | null;
  preferred_name: string | null;
  last_name: string | null;
  role_label: string | null;
  /** Works for a subcontractor (Subcontractor's crew): paid by that company, never by TCS payroll. */
  employer_provider_id?: string | null;
}

export interface InOutException {
  segment_id: string;
  rule_code: string;
}

export interface InOutInput {
  segments: InOutSegment[];
  punches: InOutPunch[];
  workers: InOutWorker[];
  exceptions: InOutException[];
}

/** The empty marker a location filter uses for "No building selected". */
export const NO_BUILDING = '__no_building__';

export interface InOutFilters {
  /** null = every employee. An empty array = nobody (the person pressed Clear). */
  workerIds: string[] | null;
  /** null = every location. Facility ids, plus NO_BUILDING for a shift with none. */
  locations: string[] | null;
}

export interface InOutDeps {
  formatPunchTime: (iso: string, tz: string | null) => string;
  deviceLabel: (mode: string | null) => string;
  isChangedByManager: (mode: string | null, supersedesEventId: string | null) => boolean;
}

/** The review flags, in the words payroll reads in the Notes column. */
export const FLAG_NO_BUILDING = 'No building selected';
export const FLAG_LONG_SHIFT = 'Shift of 9 hours or more';
export const FLAG_AUTO_CLOSED = 'Shift auto-closed after 12 hours';
/** 9 hours, in paid minutes. */
export const LONG_SHIFT_MINUTES = 9 * 60;

export interface InOutRow {
  key: string;
  segmentId: string;
  workerId: string;
  shiftDate: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  punchAt: string;
  punchTime: string;
  status: 'Time Recorded' | 'Changed By Manager';
  inOut: 'In' | 'Out';
  /** Hours for the shift — Out row only. */
  reg: number | null;
  /** Hours for the day — the day's last Out row only. */
  total: number | null;
  location: string;
  /** The worker's role label (Buddy Punch's "Dept Code"). */
  deptCode: string;
  ip: string;
  device: string;
  /** Link to the punch photo, when there is one. */
  webCam: string;
  notes: string;
  /** The shift's review flags (also written into notes). */
  flags: string[];
}

export interface InOutSummary {
  employees: number;
  punches: number;
  shifts: number;
  /** From the minutes, 3 decimals — equal to the Payroll Summary's hours. */
  totalHours: number;
  shiftsFlagged: number;
}

export interface InOutResult {
  rows: InOutRow[];
  summary: InOutSummary;
}

const round3 = (n: number) => Math.round(n * 1000) / 1000;
export const hoursFromMinutes = (m: number) => round3(m / 60);

function firstNameOf(w: InOutWorker | undefined): string {
  const p = (w?.preferred_name ?? '').trim();
  return p !== '' ? p : (w?.first_name ?? '').trim();
}

/** Every flag a shift carries, in a fixed order. */
export function shiftFlags(seg: InOutSegment, outPunch: InOutPunch | undefined, ruleCodes: Set<string>): string[] {
  const flags: string[] = [];
  if (seg.facility_id == null) flags.push(FLAG_NO_BUILDING);
  if ((seg.paid_minutes ?? 0) >= LONG_SHIFT_MINUTES) flags.push(FLAG_LONG_SHIFT);
  if (outPunch?.capture_mode === 'system_auto_close' || ruleCodes.has('daily_ceiling_auto_close')) flags.push(FLAG_AUTO_CLOSED);
  return flags;
}

/** Does this shift pass the employee and location filters? */
export function passesFilters(seg: InOutSegment, f: InOutFilters): boolean {
  if (f.workerIds != null && !f.workerIds.includes(seg.worker_id)) return false;
  if (f.locations != null) {
    const key = seg.facility_id ?? NO_BUILDING;
    if (!f.locations.includes(key)) return false;
  }
  return true;
}

export function buildInOutActivity(input: InOutInput, filters: InOutFilters, deps: InOutDeps): InOutResult {
  const punches = new Map(input.punches.map((p) => [p.punch_event_id, p]));
  const workers = new Map(input.workers.map((w) => [w.worker_id, w]));
  const rules = new Map<string, Set<string>>();
  for (const e of input.exceptions) {
    const s = rules.get(e.segment_id) ?? new Set<string>();
    s.add(e.rule_code);
    rules.set(e.segment_id, s);
  }

  // ⛔ A subcontractor's cleaner is paid by her company, not TCS (one-cleaner-list item,
  // Ben 28 Oct): she is left out here exactly as the Payroll Summary leaves her out, so this
  // report's total still equals the summary's. Her hours are on her company's pay sheet.
  const segs = input.segments
    .filter((s) => s.segment_status !== 'void')
    .filter((s) => !workers.get(s.worker_id)?.employer_provider_id)
    .filter((s) => passesFilters(s, filters));

  const rows: InOutRow[] = [];
  let flagged = 0;
  let minutes = 0;
  for (const seg of segs) {
    const w = workers.get(seg.worker_id);
    const inP = seg.in_event_id ? punches.get(seg.in_event_id) : undefined;
    const outP = seg.out_event_id ? punches.get(seg.out_event_id) : undefined;
    const flags = shiftFlags(seg, outP, rules.get(seg.segment_id) ?? new Set());
    if (flags.length > 0) flagged += 1;
    minutes += seg.paid_minutes ?? 0;

    const make = (p: InOutPunch | undefined, side: 'In' | 'Out', fallbackAt: string | null): InOutRow | null => {
      const at = p?.event_at ?? fallbackAt;
      if (!at) return null;
      const changed = deps.isChangedByManager(p?.capture_mode ?? null, p?.supersedes_event_id ?? null);
      const reason = (p && changed && p.void_reason) ? p.void_reason.trim() : '';
      return {
        key: `${seg.segment_id}:${side}`,
        segmentId: seg.segment_id,
        workerId: seg.worker_id,
        shiftDate: seg.shift_date,
        employeeId: w?.employee_id ?? '',
        firstName: firstNameOf(w),
        lastName: (w?.last_name ?? '').trim(),
        punchAt: at,
        punchTime: deps.formatPunchTime(at, seg.site_tz),
        status: changed ? 'Changed By Manager' : 'Time Recorded',
        inOut: side,
        reg: side === 'Out' && seg.ended_at != null ? hoursFromMinutes(seg.paid_minutes ?? 0) : null,
        total: null,
        location: seg.facility_id != null ? (seg.place_display ?? '') : '',
        deptCode: w?.role_label ?? '',
        ip: p?.ip_address ?? '',
        device: deps.deviceLabel(p?.capture_mode ?? null),
        webCam: p?.photo_url ?? '',
        notes: [...flags, ...(reason ? [reason] : [])].join('; '),
        flags,
      };
    };
    const inRow = make(inP, 'In', seg.started_at);
    if (inRow) rows.push(inRow);
    if (seg.ended_at != null) {
      const outRow = make(outP, 'Out', seg.ended_at);
      if (outRow) rows.push(outRow);
    }
  }

  // Sort: first name, then punch time (last name and id only break a tie
  // between two people with the same first name, so their rows never interleave).
  rows.sort((a, b) =>
    a.firstName.localeCompare(b.firstName)
    || a.lastName.localeCompare(b.lastName)
    || a.workerId.localeCompare(b.workerId)
    || Date.parse(a.punchAt) - Date.parse(b.punchAt)
    || (a.inOut === b.inOut ? 0 : a.inOut === 'In' ? -1 : 1));

  // The day's Total, on the day's last Out row — a day is a person's shift_date.
  const dayMinutes = new Map<string, number>();
  const lastOut = new Map<string, InOutRow>();
  const segMinutes = new Map(segs.map((s) => [s.segment_id, s.paid_minutes ?? 0]));
  for (const r of rows) {
    if (r.inOut !== 'Out') continue;
    const day = `${r.workerId}|${r.shiftDate}`;
    dayMinutes.set(day, (dayMinutes.get(day) ?? 0) + (segMinutes.get(r.segmentId) ?? 0));
    lastOut.set(day, r);
  }
  for (const [day, r] of lastOut) r.total = hoursFromMinutes(dayMinutes.get(day) ?? 0);

  return {
    rows,
    summary: {
      employees: new Set(segs.map((s) => s.worker_id)).size,
      punches: rows.length,
      shifts: segs.length,
      totalHours: hoursFromMinutes(minutes),
      shiftsFlagged: flagged,
    },
  };
}

/** The column headers, in Buddy Punch's order (no Est Pay). */
export const INOUT_HEADERS = [
  'Employee Id', 'First Name', 'Last Name', 'Punch Time', 'Status', 'In / Out', 'Reg', 'Total',
  'Location', 'Dept Code', 'IP Address', 'Device Used', 'Web Cam', 'Notes',
] as const;

/**
 * The rows both downloads are built from (header first, a Total row last).
 * IP & device and review flags follow the screen's toggles, so the file is
 * what the person was looking at. Hours go out as numbers, so they add up.
 */
export function inOutFileRows(result: InOutResult, opts: { showIpDevice: boolean; showFlags: boolean }): (string | number | null)[][] {
  const keep = (h: string) => {
    if (!opts.showIpDevice && (h === 'IP Address' || h === 'Device Used')) return false;
    if (!opts.showFlags && h === 'Notes') return false;
    return true;
  };
  const headers = INOUT_HEADERS.filter(keep);
  const cell = (r: InOutRow, h: string): string | number | null => {
    switch (h) {
      case 'Employee Id': return r.employeeId;
      case 'First Name': return r.firstName;
      case 'Last Name': return r.lastName;
      case 'Punch Time': return r.punchTime;
      case 'Status': return r.status;
      case 'In / Out': return r.inOut;
      case 'Reg': return r.reg;
      case 'Total': return r.total;
      case 'Location': return r.location;
      case 'Dept Code': return r.deptCode;
      case 'IP Address': return r.ip;
      case 'Device Used': return r.device;
      case 'Web Cam': return r.webCam;
      case 'Notes': return r.notes;
      default: return null;
    }
  };
  const out: (string | number | null)[][] = [[...headers]];
  for (const r of result.rows) out.push(headers.map((h) => cell(r, h)));
  out.push(headers.map((h) => {
    if (h === 'Employee Id') return 'Total — TCS App hours only';
    if (h === 'Reg' || h === 'Total') return result.summary.totalHours;
    return null;
  }));
  return out;
}

/** A worker still clocking in on Buddy Punch (their username is filled in). */
export interface BuddyPunchWorker {
  worker_id: string;
  first_name: string | null;
  preferred_name: string | null;
  last_name: string | null;
  buddy_punch_username: string | null;
}

export interface StillOnBuddyPunch {
  workerId: string;
  name: string;
  username: string;
  /** Also has app punches in the selected dates — part-way through a switch. */
  alsoInApp: boolean;
}

/**
 * The "Still on Buddy Punch" notice (Ben, option B, 2026-10-16): every ACTIVE
 * worker whose Buddy Punch username is filled in. One who also has app shifts
 * in the selected dates is marked, because people switch part-way through a
 * period. Empty list = the notice does not render.
 */
export function stillOnBuddyPunch(active: BuddyPunchWorker[], segmentsInDates: InOutSegment[]): StillOnBuddyPunch[] {
  const inApp = new Set(segmentsInDates.filter((s) => s.segment_status !== 'void').map((s) => s.worker_id));
  return active
    .filter((w) => (w.buddy_punch_username ?? '').trim() !== '')
    .map((w) => {
      const first = (w.preferred_name ?? '').trim() || (w.first_name ?? '').trim();
      return {
        workerId: w.worker_id,
        name: `${first} ${(w.last_name ?? '').trim()}`.trim(),
        username: (w.buddy_punch_username ?? '').trim(),
        alsoInApp: inApp.has(w.worker_id),
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}
